import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { formatDistanceToNow } from "date-fns";
import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  Building2,
  UserRoundCheck,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { PanelHeader } from "@/components/panel-header";
import { AdminGuard } from "@/components/role-guard";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type AdminAuditEntry =
  inferRouterOutputs<AppRouter>["getAdminAuditFeed"][number];

type SummaryTile = {
  label: string;
  value: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  href?: string;
};

const SUMMARY_SKELETON_KEYS = [
  "users-active",
  "users-total",
  "sites",
  "approvals",
  "flagged",
  "billing",
] as const;

export default function AdminDashboard() {
  const summaryQuery = useQuery({
    ...trpc.admin.summary.queryOptions(),
    placeholderData: (previousData) => previousData,
  });
  const auditQuery = useQuery({
    ...trpc.getAdminAuditFeed.queryOptions({ limit: 12 }),
    placeholderData: (previousData) => previousData,
  });
  const healthCheckQuery = useQuery(trpc.healthCheck.queryOptions());

  const summaryTiles = useMemo<SummaryTile[]>(() => {
    if (!summaryQuery.data) {
      return [];
    }

    const siteStatusLabel = summaryQuery.data.siteStatusCounts
      .map((status) => `${status.count} ${startCase(status.status)}`)
      .join(" · ");

    return [
      {
        label: "Active users",
        value: formatCount(summaryQuery.data.activeUsers24h),
        description: "Sessions active in the last 24 hours.",
        icon: Users,
        href: "/admin/users",
      },
      {
        label: "Total users",
        value: formatCount(summaryQuery.data.totalUsers),
        description: `Workers ${summaryQuery.data.roleCounts.worker} · Site managers ${summaryQuery.data.roleCounts.site_manager} · Admins ${summaryQuery.data.roleCounts.admin}`,
        icon: UserRoundCheck,
        href: "/admin/users",
      },
      {
        label: "Sites",
        value: formatCount(
          summaryQuery.data.siteStatusCounts.reduce(
            (total, row) => total + row.count,
            0
          )
        ),
        description: siteStatusLabel || "No sites recorded yet.",
        icon: Building2,
        href: "/admin/sites",
      },
      {
        label: "Pending approvals",
        value: formatCount(
          summaryQuery.data.pendingSiteManagers +
            summaryQuery.data.pendingWorkerApprovals
        ),
        description: `${summaryQuery.data.pendingSiteManagers} site managers · ${summaryQuery.data.pendingWorkerApprovals} workers`,
        icon: BadgeCheck,
        href: "/admin/users",
      },
      {
        label: "Flagged sign-ins",
        value: formatCount(summaryQuery.data.flaggedSignIns24h),
        description:
          "Outside-geofence or flagged sign-ins in the last 24 hours.",
        icon: AlertTriangle,
        href: "/admin/sites",
      },
      {
        label: "Billing overrides",
        value: formatCount(summaryQuery.data.activeBypassOverrides),
        description: "Managers currently bypassing subscription enforcement.",
        icon: Activity,
        href: "/admin/billing-oversight",
      },
    ];
  }, [summaryQuery.data]);

  return (
    <AdminGuard>
      <div className="relative isolate min-h-full overflow-hidden">
        <div className="fixed inset-0 bg-[#f7f7f7] bg-[image:var(--dashboard-hero-image)] bg-cover bg-center bg-no-repeat" />
        <PageContainer
          variant="dashboard"
          className="relative z-10 flex min-h-full flex-col gap-6"
        >
          <PageHeader
            title="Admin dashboard"
            description="Review live platform state and inspect the latest admin activity."
            tags={<HealthBadge status={healthCheckQuery.data} />}
          />

          {summaryQuery.isError ? (
            <InlineError message="Unable to load the admin summary right now." />
          ) : (
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {summaryQuery.isLoading && summaryTiles.length === 0
                ? SUMMARY_SKELETON_KEYS.map((key) => (
                    <Card key={key}>
                      <CardHeader className="pb-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-8 w-20" />
                      </CardHeader>
                      <CardContent>
                        <Skeleton className="h-4 w-full" />
                      </CardContent>
                    </Card>
                  ))
                : summaryTiles.map((tile) => (
                    <SummaryCard key={tile.label} tile={tile} />
                  ))}
            </section>
          )}

          <RecentActivitySection
            entries={auditQuery.data ?? []}
            isLoading={auditQuery.isLoading}
            isError={auditQuery.isError}
          />
        </PageContainer>
      </div>
    </AdminGuard>
  );
}

function SummaryCard({ tile }: { tile: SummaryTile }) {
  const content = (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div className="space-y-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {tile.label}
          </CardTitle>
          <div className="text-3xl font-semibold text-foreground">
            {tile.value}
          </div>
        </div>
        <div className="icon-tile">
          <tile.icon className="h-5 w-5" aria-hidden />
        </div>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        {tile.description}
      </CardContent>
    </Card>
  );

  if (!tile.href) {
    return content;
  }

  return (
    <Link to={tile.href} className="block h-full">
      {content}
    </Link>
  );
}

function RecentActivitySection({
  entries,
  isLoading,
  isError,
}: {
  entries: AdminAuditEntry[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <PanelHeader
          title="Recent admin activity"
          description="Latest platform and form events captured in the audit feed."
          icon={<Activity className="size-4" />}
        />
      </CardHeader>
      <CardContent>
        {isError ? (
          <InlineError message="Unable to load recent admin activity." />
        ) : isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : entries.length === 0 ? (
          <Empty className="min-h-[18rem]">
            <EmptyTitle>No audit events yet</EmptyTitle>
            <EmptyDescription>
              Recent platform changes will appear here once admins or site
              managers take action.
            </EmptyDescription>
          </Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium text-foreground">
                    {startCase(entry.action)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.actorLabel}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{entry.source}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.entityType}
                    {entry.entityId ? ` · ${entry.entityId}` : ""}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatRelative(entry.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function HealthBadge({ status }: { status: string | undefined }) {
  if (!status) {
    return <Badge variant="outline">API checking</Badge>;
  }

  return (
    <Badge variant={status === "OK" ? "approved" : "warning"}>
      API {status === "OK" ? "connected" : status}
    </Badge>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
      {message}
    </div>
  );
}

function formatCount(value: number) {
  return new Intl.NumberFormat("en-GB").format(value);
}

function formatRelative(value: string | Date | null | undefined) {
  if (!value) {
    return "recently";
  }

  return formatDistanceToNow(new Date(value), { addSuffix: true });
}

function startCase(value: string) {
  return value
    .replaceAll("_", " ")
    .replaceAll(".", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}
